import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import {
    TbClipboardList,
    TbFileCheck,
    TbAlertTriangle,
    TbCashBanknote,
    TbShoppingCart,
    TbTruckDelivery,
} from 'react-icons/tb';
import { format, subDays, addWeeks, differenceInCalendarDays } from 'date-fns';
import StatCard from './StatCard';
import RingChart from './RingChart';
import BreakdownChips from './BreakdownChips';
import BarRow from './BarRow';
import Sparkline from './Sparkline';
import { buildDailySeries, buildDayLabels, weekOverWeekLabel } from './activityUtils';
import useCountUp from './useCountUp';
import { Delivery, useGetDeliveries, useGetDeliveriesActivity } from '../../queries/Delivery';
import { useGetOrders, useGetOrdersActivity } from '../../queries/Orders';
import { useGetRequisitions, useGetRequisitionsActivity } from '../../queries/Requisitions';
import { useCurrentBusinessUnitId, useCurrentProjectId, useUser } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';
import Loader from '../../components/Loader';
import { formatCurrencyAmount, fomatCurrencySymbol } from '../../utils/Utils';

const ACTIVITY_DAYS = 14;
const getDayBefore = () => format(subDays(new Date(), 1), 'yyyy-MM-dd');
const getNextWeekDate = () => format(addWeeks(new Date(), 1), 'yyyy-MM-dd');

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Dashboard(props: RouteComponentProps) {
    const user = useUser();
    const projectId = useCurrentProjectId();
    const businessUnitId = useCurrentBusinessUnitId();

    const getPermissions = useGetPermissions('PROJECT', projectId.data);

    const [permissions, setPermissions] = useState(getPermissions.data);
    const [lazy, setLazy] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (projectId.data && businessUnitId.data && user && getPermissions.data) {
            setPermissions(getPermissions.data);
            setLazy(false);
        }
    }, [projectId.data, businessUnitId.data, user, getPermissions.data]);

    const canSeeExportedOrders = permissions?.items.find(
        (p) => p.code === 'Project_Show_Exported_Orders'
    )?.hasAccess;
    const ordersExportedFilter = canSeeExportedOrders ? undefined : false;

    const canSeeExportedDeliveries = permissions?.items.find(
        (p) => p.code === 'Project_Show_Exported_Deliveries'
    )?.hasAccess;
    const deliveriesExportedFilter = canSeeExportedDeliveries ? undefined : false;

    const canShowRequisitions = permissions?.items.find(
        (p) => p.code === 'Project_Show_Requisitions_Dashboard'
    )?.hasAccess;
    const canShowDeliveries = permissions?.items.find(
        (p) => p.code === 'Project_Show_Deliveries_Dashboard'
    )?.hasAccess;

    // Requisitions: independent counts per workflow state (not mutually
    // exclusive, so shown as separate bars rather than a stacked 100% total).
    const reqPending = useGetRequisitions(
        0,
        1,
        projectId.data,
        false,
        false,
        undefined,
        undefined,
        false,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashReqPending'
    );
    const reqReview = useGetRequisitions(
        0,
        1,
        projectId.data,
        undefined,
        true,
        undefined,
        undefined,
        false,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashReqReview'
    );
    const reqApproved = useGetRequisitions(
        0,
        1,
        projectId.data,
        true,
        undefined,
        undefined,
        false,
        false,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashReqApproved'
    );
    const reqCompleted = useGetRequisitions(
        0,
        1,
        projectId.data,
        undefined,
        undefined,
        undefined,
        true,
        false,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashReqCompleted'
    );

    // Reconciliation progress: reconciled vs. still-to-reconcile, excluding
    // disputed deliveries (those are blocked from recon and shown separately).
    const reconYes = useGetDeliveries(
        0,
        1,
        undefined,
        undefined,
        undefined,
        projectId.data,
        businessUnitId.data,
        false,
        deliveriesExportedFilter,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        'dashReconYes'
    );
    const reconNo = useGetDeliveries(
        0,
        1,
        undefined,
        undefined,
        undefined,
        projectId.data,
        businessUnitId.data,
        false,
        deliveriesExportedFilter,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        'dashReconNo'
    );

    // Disputes: fetched with a generous (uncapped-in-practice) take so the
    // aging breakdown below can be computed from the same call as the count.
    const disputesRich = useGetDeliveries(
        0,
        300,
        undefined,
        undefined,
        undefined,
        projectId.data,
        businessUnitId.data,
        true,
        deliveriesExportedFilter,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashDisputes'
    );

    // Unpaid deliveries due in the same window the tile has always used
    // (from yesterday through a week out) - same reasoning, richer response.
    const unpaidRich = useGetDeliveries(
        0,
        300,
        undefined,
        undefined,
        undefined,
        projectId.data,
        businessUnitId.data,
        false,
        false,
        lazy,
        undefined,
        undefined,
        new Date(getDayBefore()),
        new Date(getNextWeekDate()),
        undefined,
        'dashUnpaid'
    );

    const ordersPending = useGetOrders(
        0,
        300,
        undefined,
        projectId.data,
        businessUnitId.data,
        undefined,
        false,
        false,
        ordersExportedFilter,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashOrdersPending'
    );
    const ordersApproved = useGetOrders(
        0,
        1,
        undefined,
        projectId.data,
        businessUnitId.data,
        undefined,
        false,
        true,
        ordersExportedFilter,
        lazy,
        undefined,
        undefined,
        undefined,
        undefined,
        'dashOrdersApproved'
    );

    const activityDateFrom = subDays(new Date(), ACTIVITY_DAYS - 1);
    const activityDateTo = new Date();

    const reqActivity = useGetRequisitionsActivity(
        projectId.data,
        activityDateFrom,
        activityDateTo,
        lazy,
        'dashReqActivity'
    );
    const orderActivity = useGetOrdersActivity(
        projectId.data,
        businessUnitId.data,
        activityDateFrom,
        activityDateTo,
        lazy,
        'dashOrderActivity'
    );
    const deliveryActivity = useGetDeliveriesActivity(
        projectId.data,
        businessUnitId.data,
        activityDateFrom,
        activityDateTo,
        lazy,
        'dashDeliveryActivity'
    );

    const allQueries = [
        reqPending,
        reqReview,
        reqApproved,
        reqCompleted,
        reconYes,
        reconNo,
        disputesRich,
        unpaidRich,
        ordersPending,
        ordersApproved,
        reqActivity,
        orderActivity,
        deliveryActivity,
    ];

    // On the very first successful load, each query's own lazy-flip-triggered
    // auto-fetch already covers the initial request - resetting here too would
    // fire every query a second time. Only force a reset (to bust d-man's
    // dedup and refetch with the new context) once we've already loaded once,
    // e.g. when the user switches project/business unit.
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        if (permissions && !lazy) {
            if (hasLoadedOnceRef.current) {
                allQueries.forEach((q) => q.reset());
            }
            hasLoadedOnceRef.current = true;
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissions, lazy]);

    // --- Requisitions ---
    const reqPendingCount = reqPending.data?.totalCount || 0;
    const reqReviewCount = reqReview.data?.totalCount || 0;
    const reqApprovedCount = reqApproved.data?.totalCount || 0;
    const reqCompletedCount = reqCompleted.data?.totalCount || 0;
    const reqMax = Math.max(reqReviewCount, reqApprovedCount, reqCompletedCount, 1);
    const reqPendingAnimated = useCountUp(reqPendingCount);
    const reqVisible = ![reqPending, reqReview, reqApproved, reqCompleted].some((q) => q.loading);

    // --- Reconciliation ---
    const reconYesCount = reconYes.data?.totalCount || 0;
    const reconNoCount = reconNo.data?.totalCount || 0;
    const reconTotal = reconYesCount + reconNoCount;
    const reconPercent = reconTotal > 0 ? (reconYesCount / reconTotal) * 100 : 0;
    const reconNoAnimated = useCountUp(reconNoCount);
    const reconVisible = !reconYes.loading && !reconNo.loading;

    // --- Disputes ---
    const disputeItems = disputesRich.data?.items || [];
    const disputeCount = disputesRich.data?.totalCount || 0;
    const disputeAges = disputeItems
        .map((d) => {
            const openDispute = d.disputes?.find((dispute) => !dispute.resolvedAt);
            return openDispute?.createdAt
                ? differenceInCalendarDays(new Date(), new Date(openDispute.createdAt))
                : null;
        })
        .filter((n): n is number => n !== null);
    const disputeBuckets = [
        {
            label: '≤3 days',
            value: disputeAges.filter((a) => a <= 3).length,
            colorClassName: 'bg-success',
        },
        {
            label: '4-7 days',
            value: disputeAges.filter((a) => a > 3 && a <= 7).length,
            colorClassName: 'bg-warning',
        },
        {
            label: '8+ days',
            value: disputeAges.filter((a) => a > 7).length,
            colorClassName: 'bg-danger',
        },
    ];
    const oldestDisputeDays = disputeAges.length ? Math.max(...disputeAges) : 0;
    const disputeSampleTruncated = disputeCount > disputeItems.length;
    const disputeCountAnimated = useCountUp(disputeCount);
    const disputesVisible = !disputesRich.loading;

    // --- Unpaid deliveries ---
    const unpaidItems = (unpaidRich.data?.items || []) as (Delivery & { amount?: number })[];
    const unpaidCount = unpaidRich.data?.totalCount || 0;
    const unpaidTotalValue = unpaidItems.reduce((sum, d) => sum + (d.amount || 0), 0);
    const now = new Date();
    const unpaidOverdueCount = unpaidItems.filter((d) => d.payByDate && new Date(d.payByDate) < now)
        .length;
    const unpaidDueSoonCount = unpaidCount - unpaidOverdueCount;
    const unpaidValueAnimated = useCountUp(unpaidTotalValue);
    const unpaidSampleTruncated = unpaidCount > unpaidItems.length;
    const unpaidVisible = !unpaidRich.loading;

    // --- Orders ---
    const ordersPendingItems = ordersPending.data?.items || [];
    const ordersPendingCount = ordersPending.data?.totalCount || 0;
    const ordersPendingValue = ordersPendingItems.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const ordersApprovedCount = ordersApproved.data?.totalCount || 0;
    const ordersValueAnimated = useCountUp(ordersPendingValue);
    const ordersSampleTruncated = ordersPendingCount > ordersPendingItems.length;
    const ordersVisible = !ordersPending.loading && !ordersApproved.loading;

    // --- Activity ---
    const activityDayLabels = buildDayLabels(ACTIVITY_DAYS);
    const reqActivitySeries = buildDailySeries(reqActivity.data, ACTIVITY_DAYS);
    const orderActivitySeries = buildDailySeries(orderActivity.data, ACTIVITY_DAYS);
    const deliveryActivitySeries = buildDailySeries(deliveryActivity.data, ACTIVITY_DAYS);
    const reqActivityTotal = useCountUp(reqActivitySeries.reduce((a, b) => a + b, 0));
    const orderActivityTotal = useCountUp(orderActivitySeries.reduce((a, b) => a + b, 0));
    const deliveryActivityTotal = useCountUp(deliveryActivitySeries.reduce((a, b) => a + b, 0));
    const reqActivityVisible = !reqActivity.loading;
    const orderActivityVisible = !orderActivity.loading;
    const deliveryActivityVisible = !deliveryActivity.loading;

    return (
        <div className="p-6">
            <div className="mb-5">
                <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
                <p className="text-sm text-foreground-secondary mt-0.5">
                    {format(new Date(), 'EEEE, d MMMM yyyy')}
                </p>
            </div>

            {(loading || !permissions) && <Loader message="Loading dashboard..." />}
            {user && !loading && projectId.data && businessUnitId.data && permissions && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {canShowRequisitions && (
                        <StatCard
                            title="Requisitions"
                            icon={TbClipboardList}
                            iconClassName="bg-transparent border border-border text-orange-500"
                            headline={reqPendingAnimated}
                            subtitle="Pending action"
                            url={reqPendingCount ? '/requisitions?status=draft' : '/requisitions'}
                            visible={reqVisible}
                            index={0}
                        >
                            <div className="flex flex-col gap-2">
                                <BarRow
                                    label="In review"
                                    value={reqReviewCount}
                                    max={reqMax}
                                    colorClassName="bg-warning"
                                />
                                <BarRow
                                    label="Approved"
                                    value={reqApprovedCount}
                                    max={reqMax}
                                    colorClassName="bg-orange-500"
                                />
                                <BarRow
                                    label="Completed"
                                    value={reqCompletedCount}
                                    max={reqMax}
                                    colorClassName="bg-success"
                                />
                            </div>
                        </StatCard>
                    )}

                    {canShowDeliveries && (
                        <StatCard
                            title="Reconciliation"
                            icon={TbFileCheck}
                            iconClassName="bg-transparent border border-border text-success"
                            headline={reconNoAnimated}
                            subtitle="Still to reconcile"
                            url="/deliveries"
                            visible={reconVisible}
                            index={1}
                        >
                            <div className="flex items-center gap-3">
                                <RingChart
                                    percent={reconPercent}
                                    colorClassName="text-success"
                                    centerLabel={`${Math.round(reconPercent)}%`}
                                />
                                <div className="text-xs text-foreground-secondary">
                                    <span className="font-semibold text-foreground">
                                        {reconYesCount}
                                    </span>{' '}
                                    of {reconTotal} reconciled
                                </div>
                            </div>
                        </StatCard>
                    )}

                    {canShowDeliveries && (
                        <StatCard
                            title="Disputes"
                            icon={TbAlertTriangle}
                            iconClassName="bg-transparent border border-border text-danger"
                            headline={disputeCountAnimated}
                            headlineClassName={disputeCount > 0 ? 'text-danger' : 'text-foreground'}
                            subtitle={
                                disputeCount > 0
                                    ? `Oldest open: ${oldestDisputeDays} day${
                                          oldestDisputeDays === 1 ? '' : 's'
                                      }${disputeSampleTruncated ? ' (sampled)' : ''}`
                                    : 'No open disputes'
                            }
                            url="/deliveries?isDisputed=true"
                            visible={disputesVisible}
                            index={2}
                        >
                            {disputeCount > 0 && <BreakdownChips chips={disputeBuckets} />}
                        </StatCard>
                    )}

                    {canShowDeliveries && (
                        <StatCard
                            title="Unpaid Deliveries"
                            icon={TbCashBanknote}
                            iconClassName="bg-transparent border border-border text-warning"
                            headline={`${fomatCurrencySymbol('ZAR')} ${formatCurrencyAmount(
                                unpaidValueAnimated
                            )}`}
                            subtitle={`${unpaidCount} deliver${unpaidCount === 1 ? 'y' : 'ies'}${
                                unpaidSampleTruncated ? ' (sampled)' : ''
                            } due this week`}
                            url="/deliveries"
                            visible={unpaidVisible}
                            index={3}
                        >
                            {unpaidCount > 0 && (
                                <BreakdownChips
                                    chips={[
                                        {
                                            label: 'Overdue',
                                            value: unpaidOverdueCount,
                                            colorClassName: 'bg-danger',
                                        },
                                        {
                                            label: 'Due this week',
                                            value: unpaidDueSoonCount,
                                            colorClassName: 'bg-warning',
                                        },
                                    ]}
                                />
                            )}
                        </StatCard>
                    )}

                    <StatCard
                        title="Orders"
                        icon={TbShoppingCart}
                        iconClassName="bg-transparent border border-border text-orange-500"
                        headline={`${fomatCurrencySymbol('ZAR')} ${formatCurrencyAmount(
                            ordersValueAnimated
                        )}`}
                        subtitle={`${ordersPendingCount} pending approval${
                            ordersSampleTruncated ? ' (sampled)' : ''
                        }`}
                        url={ordersPendingCount ? '/orders?status=draft' : '/orders'}
                        visible={ordersVisible}
                        index={4}
                    >
                        {(ordersPendingCount > 0 || ordersApprovedCount > 0) && (
                            <BreakdownChips
                                chips={[
                                    {
                                        label: 'Pending',
                                        value: ordersPendingCount,
                                        colorClassName: 'bg-orange-500',
                                    },
                                    {
                                        label: 'Approved',
                                        value: ordersApprovedCount,
                                        colorClassName: 'bg-success',
                                    },
                                ]}
                            />
                        )}
                    </StatCard>
                </div>
            )}

            {user && !loading && projectId.data && businessUnitId.data && permissions && (
                <>
                    <h2 className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mt-6 mb-3">
                        Activity &middot; last {ACTIVITY_DAYS} days
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <StatCard
                            title="Requisitions"
                            icon={TbClipboardList}
                            iconClassName="bg-transparent border border-border text-orange-500"
                            headline={reqActivityTotal}
                            subtitle={weekOverWeekLabel(reqActivitySeries) || 'No activity yet'}
                            url="/requisitions"
                            visible={reqActivityVisible}
                            index={5}
                        >
                            <Sparkline
                                data={reqActivitySeries}
                                labels={activityDayLabels}
                                colorClassName="text-orange-500"
                            />
                        </StatCard>
                        <StatCard
                            title="Orders"
                            icon={TbShoppingCart}
                            iconClassName="bg-transparent border border-border text-chart-teal"
                            headline={orderActivityTotal}
                            subtitle={weekOverWeekLabel(orderActivitySeries) || 'No activity yet'}
                            url="/orders"
                            visible={orderActivityVisible}
                            index={6}
                        >
                            <Sparkline
                                data={orderActivitySeries}
                                labels={activityDayLabels}
                                colorClassName="text-chart-teal"
                            />
                        </StatCard>
                        <StatCard
                            title="Deliveries"
                            icon={TbTruckDelivery}
                            iconClassName="bg-transparent border border-border text-chart-violet"
                            headline={deliveryActivityTotal}
                            subtitle={
                                weekOverWeekLabel(deliveryActivitySeries) || 'No activity yet'
                            }
                            url="/deliveries"
                            visible={deliveryActivityVisible}
                            index={7}
                        >
                            <Sparkline
                                data={deliveryActivitySeries}
                                labels={activityDayLabels}
                                colorClassName="text-chart-violet"
                            />
                        </StatCard>
                    </div>
                </>
            )}
        </div>
    );
}
